import { FaqBlock } from "@/components/site/FaqBlock";
import { faqFor } from "@/content/faq-blocks";
import { seoLinks, absoluteUrl, ogImage } from "@/lib/seo";
import { createFileRoute, Link } from "@tanstack/react-router";

import { CtaBand, PageHero, Prose } from "../../components/site/Page";
import sofia from "../../assets/sofia-distans.png";
import hermods from "../../assets/hermods.png";

const title = "Secondary School & Sixth Form | Swedish International School";
const description =
  "Study lower secondary via Sofia Distans and upper secondary via Hermods at our premises in Estepona, with on-site supervisors, meals, activities and community.";

export const Route = createFileRoute("/en/secondary-school-sixth-form")({
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "article" },
      { property: "og:url", content: absoluteUrl("/en/secondary-school-sixth-form") },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:locale", content: "en_GB" },
      ...ogImage(),
    ],
    links: seoLinks("/en/secondary-school-sixth-form"),
  }),
  component: SecondarySchoolSixthForm,
});

function SecondarySchoolSixthForm() {
  return (
    <>
      <PageHero
        eyebrow="Years 7–9 and sixth form"
        title="Secondary school & sixth form"
        intro="Lower and upper secondary pupils study by distance learning via Sofia Distans and Hermods respectively – with supervisors, facilities, meals and community with us in Estepona."
      />

      <Prose>
        <h2>Lower secondary via Sofia Distans</h2>
        <img
          src={sofia}
          alt="Sofia Distans logo"
          width={140}
          height={140}
          loading="lazy"
          className="h-24 w-auto"
        />
        <p>
          For lower secondary pupils, we refer to Sofia Distans, a distance-learning school based in
          Stockholm that, on behalf of the Swedish National Agency for Education (Skolverket), offers
          lower secondary studies for Swedish pupils abroad. Pupils can study at our premises with
          our supervisors, but receive their material and planning from Sofia Distans.
        </p>
        <p>
          Teachers in Stockholm at Sofia Distans assess tests and assignments and set all grades. Two
          applications are made: one to Sofia Distans and one to Swedish International School. This
          means two school fees – one to Sofia Distans, where a grant from Skolverket may
          significantly reduce that fee. In addition, you pay a{" "}
          <Link to={"/en/fees" as never}>fee</Link> to our school for premises, supervision, meals,
          activities and more.
        </p>
        <p>
          Pupils studying by distance work independently but are still part of a community within the
          lower secondary class and take part in the school's various activities and events.
        </p>
        <p>
          Welcome to attend lower secondary with us.{" "}
          <Link to={"/en/application" as never}>Apply here.</Link>{" "}
          <a
            href="https://sofiadistans.stockholm.se/ansok-till-sofia-distans/"
            target="_blank"
            rel="noopener noreferrer"
          >
            Read more about Sofia Distans and apply to the school here.
          </a>
        </p>

        <h2>Sixth form via Hermods</h2>
        <img
          src={hermods}
          alt="Hermods logo"
          width={200}
          height={52}
          loading="lazy"
          className="h-12 w-auto"
        />
        <p>
          Hermods Distansgymnasium offers upper secondary education by distance learning for young
          people living abroad as a result of at least one parent's work. Hermods Distansgymnasium
          operates on behalf of Skolverket.
        </p>
        <p>At Hermods, pupils can study the following university-preparatory programmes:</p>
        <ul>
          <li>Natural Science programme</li>
          <li>Social Science programme</li>
          <li>Business programme</li>
          <li>Technology programme</li>
          <li>Humanities programme</li>
        </ul>
        <p>
          Hermods also offers over 150 individual upper secondary courses. As Hermods
          Distansgymnasium has no limit on places, all eligible pupils belonging to the target group
          of Swedish sixth-form pupils abroad are admitted.
        </p>
        <p>
          Applications for a place at our premises with supervision for Hermods distance studies are
          made <Link to={"/en/application" as never}>here</Link>.{" "}
          <a href="https://hermods.se/gymnasium/anmalan/" target="_blank" rel="noopener noreferrer">
            Read more about Hermods and apply to the school here.
          </a>
        </p>

        <h2>State grants</h2>
        <p>
          To help finance studies, it is possible to apply to Skolverket for a state grant for Swedish
          education abroad. The guardian's employment must meet certain conditions to qualify for the
          grant. It is therefore only possible to apply for the state grant for families where the
          pupil accompanies a guardian.
        </p>
        <p>
          Criteria for the state grant and application forms can be found on{" "}
          <a
            href="https://www.skolverket.se/skolutveckling/statsbidrag/statsbidrag-for-distansundervisning-2023"
            target="_blank"
            rel="noopener noreferrer"
          >
            Skolverket's website
          </a>
          . You will also find the forms to fill in there.
        </p>

        <h2>What the study day looks like</h2>
        <p>
          Pupils arrive in the morning and work on their courses at the school during the day. The
          supervisor helps with weekly planning, structure, deadlines and sorting out questions in the
          material, while subject teachers at Sofia Distans and Hermods are responsible for teaching,
          assessment and grades. Lunch is served at school on regular school days and the day includes breaks and time for movement.
        </p>
        <p>
          Distance study requires independence, but the pupil is not alone. Sitting in a group with
          other pupils, with adults present and fixed hours, makes it easier to keep a steady pace
          across the term.
        </p>

        <h2>Combining with sport</h2>
        <p>
          Many of our older pupils combine their studies with the{" "}
          <a className="text-primary link-quiet" href="/en/football-academy">football academy</a>{" "}
          or the{" "}
          <a className="text-primary link-quiet" href="/en/padel-tennis">padel and tennis programme</a>.
          The timetable is arranged so training and study fit together, and because courses are studied
          at a distance there is some flexibility around matches and tournaments.
        </p>

        <h2>Good to know before applying</h2>
        <ul>
          <li>Two applications are required: one to the distance school and one to us.</li>
          <li>Eligibility and course choices are handled by Sofia Distans and Hermods.</li>
          <li>Bring previous grades and study plans so we can plan the setup with you.</li>
          <li>Our fee covers premises, supervision, meals and activities — see{" "}
            <a className="text-primary link-quiet" href="/en/fees">fees</a>.</li>
        </ul>


      </Prose>

      <FaqBlock items={faqFor("hogstadium", "en")} locale={"en"} />

      <CtaBand />
      <div className="h-20" />
    </>
  );
}
